import type { CSSProperties } from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import classNames from 'classnames';

import { useDndRef } from '@iconik-io/shared-lib/src/react/hooks/use_dnd_ref';

import type { PickedFile } from '../../ducks/uploader/types';
import Icon, { ICONS } from '../../icons/icon';
import { useAppSelector } from '../../store/react_redux_typed';
import type { FCC } from '../../types/common';
import { createPickedFile } from '../../utils/create_picked_file';
import fileSystemEntryRead from '../../utils/directory_read';
import { getPlayOriginalDuringTranscoding } from '../unified_player/selectors';

import styles from './files_dropzone.module.css';

type Props = {
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  onDropFiles?: (files: PickedFile[]) => void;
};

// A directory listing mixes files and directories, and only a file entry
// answers to `file()`.
const isFileEntry = (entry: FileSystemEntry): entry is FileSystemFileEntry =>
  entry.isFile;

const FilesDropzone: FCC<Props> = (props) => {
  const { className, style } = props;
  const shouldReadFileMetadata = useAppSelector(
    getPlayOriginalDuringTranscoding,
  );
  const [collectedProps, dropRef] = useDrop(
    () => ({
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.canDrop() && monitor.isOver(),
      }),
      accept: NativeTypes.FILE,
      drop: (dropItem: { files: File[]; items: DataTransferItem[] }) => {
        const isEmptyFiles = dropItem.files.length < 1;
        if (!props.onDropFiles || isEmptyFiles) {
          return;
        }

        const items = Array.from(dropItem.items);
        Promise.all(
          items.map((item) => fileSystemEntryRead(item.webkitGetAsEntry())),
        ).then((results) => {
          const fileEntries = results.flat();
          const filesPromise = Promise.all(
            fileEntries.filter(isFileEntry).map(
              (fileEntry) =>
                new Promise<PickedFile>((resolve, reject) => {
                  fileEntry.file((file: File) => {
                    resolve(
                      createPickedFile(
                        file,
                        shouldReadFileMetadata,
                        fileEntry.fullPath,
                      ),
                    );
                  }, reject);
                }),
            ),
          );
          filesPromise.then((files) => {
            props.onDropFiles?.(files);
          });
        });
      },
      canDrop: () => !props.disabled,
    }),
    [props.disabled],
  );

  const dropTargetRef = useDndRef(dropRef);

  const activeClassName = collectedProps.canDrop ? styles.active : undefined;
  return (
    <div
      ref={dropTargetRef}
      style={style}
      className={classNames(className, activeClassName)}
    >
      {collectedProps.canDrop && collectedProps.isOver && (
        <div className={styles.active_message}>
          <div className={styles.active_message_content}>
            <Icon icon={ICONS.Upload} size={200} className={styles.file_icon} />
          </div>
        </div>
      )}
      {props.children}
    </div>
  );
};

export default FilesDropzone;
